import { Box, Button, styled, Tooltip } from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import { MgSupplierItemCard } from '@retail/products/types';
import { Dispatch, SetStateAction } from 'react';

interface Props {
  suppliers: Partial<MgSupplierItemCard>[];
  selectedSupplier: Partial<MgSupplierItemCard> | null;
  setSelectedSupplier: Dispatch<SetStateAction<Partial<MgSupplierItemCard> | null>>;
}

export const SupplierButtonGroup = ({
  suppliers,
  selectedSupplier,
  setSelectedSupplier,
}: Props) => {
  const t = useProductsTFunction();

  return (
    <Box display="flex" px={2}>
      <Tooltip title={t('products.supplier.allSuppliers')}>
        <SupplierButton
          isActive={!selectedSupplier}
          onClick={() => setSelectedSupplier(null)}
          isSupplierButton
        >
          <EllipsisBox>{t('products.supplier.allSuppliers')}</EllipsisBox>
        </SupplierButton>
      </Tooltip>

      {suppliers.map((supplier) => {
        const shortenedSupplierName =
          supplier?.mgSupplier?.name && supplier.mgSupplier?.name.length > 15
            ? `${supplier.mgSupplier?.name.slice(0, 15)}...`
            : supplier.mgSupplier?.name;

        return (
          <Tooltip
            key={`${supplier.mgSupplier?.mgSupplierId}_${supplier.mgSupplierItemId}`}
            title={supplier.mgSupplier?.name}
          >
            <SupplierButton
              sx={{ marginLeft: 1 }}
              isActive={
                selectedSupplier?.mgSupplier?.mgSupplierId === supplier.mgSupplier?.mgSupplierId &&
                selectedSupplier?.mgSupplierItemId === supplier.mgSupplierItemId
              }
              onClick={() => setSelectedSupplier(supplier)}
            >
              <EllipsisBox>{shortenedSupplierName}</EllipsisBox>
            </SupplierButton>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const SupplierButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isSupplierButton',
})<{ isActive?: boolean; isSupplierButton?: boolean }>(({ isActive, isSupplierButton, theme }) => {
  const palette = theme.vars.palette;
  return {
    background: isActive ? palette.grey[800] : palette.background.paper,
    border: `2px solid ${palette.grey[isActive ? 800 : 100]}`,
    borderRadius: Number(theme.shape.borderRadius) * 6,
    color: isActive ? palette.common.white : palette.grey[700],
    maxWidth: isSupplierButton ? 250 : 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    '&:hover': {
      borderWidth: '2px',
    },
    ...(isActive && theme.applyStyles('dark', { color: palette.background.default })),
  };
});

const EllipsisBox = styled(Box)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
